/**
 * Sets a value in a nested object using dot notation path
 * Supports array indices (e.g., 'resume.educations.0.studyType')
 * @example setNestedValue(obj, 'user.firstName', 'John')
 * @example setNestedValue(obj, 'resume.educations.0.studyType', 'Bachelor')
 */
function setNestedValue(obj: any, path: string, value: any, fieldName?: string): void {
  const keys = path.split(".")
  const lastKey = keys.pop()!

  let current = obj
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const nextKey = keys[i + 1]

    if (!current[key]) {
      // Check if next key is a number to determine if we need an array
      const isNextKeyNumeric = nextKey !== undefined && /^\d+$/.test(nextKey)
      current[key] = isNextKeyNumeric ? [] : {}
    }
    current = current[key]
  }

  // Special handling for customFields: create {name, value} object
  if (lastKey === "customFields" && fieldName) {
    if (!Array.isArray(current[lastKey])) {
      current[lastKey] = []
    }
    current[lastKey].push({
      name: fieldName,
      value: value,
    })
  } else {
    current[lastKey] = value
  }
}

/**
 * Converts a File object to base64 string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = reader.result as string
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64Data = base64.split(",")[1]
      resolve(base64Data)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Transforms file uploads to ApplicationFile format
 */
async function transformFileUpload(files: File[]): Promise<ApplicationFile[]> {
  if (!files || files.length === 0) return []

  return Promise.all(
    files.map(async (file) => ({
      filename: file.name,
      filedata: await fileToBase64(file),
      mimeType: file.type,
    }))
  )
}

/**
 * Applies transformation based on field type
 */
async function transformByFieldType(value: any, fieldType: string): Promise<any> {
  if (fieldType === "upload") {
    return transformFileUpload(value)
  }
  return value
}

/**
 * Builds an ApplicationRequest from form data and form config
 */
export async function buildApplicationRequest(
  formData: Record<string, any>,
  formConfig: FormConfig,
  staticData: {
    jobID: string
    apiConfigurationName: string | null
    applicationType: ApplicationType
  },
  customFields?: CustomField[]
): Promise<ApplicationRequest> {
  const request: Partial<ApplicationRequest> = {
    jobID: staticData.jobID,
    apiConfigurationName: staticData.apiConfigurationName,
    applicationType: staticData.applicationType,
  }

  // Process each field according to its path
  for (const field of formConfig.form.fields) {
    const value = formData[field.name]

    if (value === undefined || value === null) continue

    if (field.path) {
      // Transform based on field type
      const transformedValue = await transformByFieldType(value, field.type)

      setNestedValue(request, field.path, transformedValue, field.name)
    }
  }

  // Merge query header customFields with existing form customFields
  if (customFields && customFields.length > 0) {
    if (!request.other) {
      request.other = {}
    }

    // If customFields already exist from form fields, append to them
    if (request.other.customFields && Array.isArray(request.other.customFields)) {
      request.other.customFields = [...request.other.customFields, ...customFields]
    } else {
      request.other.customFields = customFields
    }
  }

  return request as ApplicationRequest
}
